import React, { useEffect, useState } from "react";
import { UserCircle, KeyRound, Mail, Save, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminProfile() {
  const { user, refresh } = useAuth();
  const [recovery, setRecovery] = useState(user?.recovery_email || "");
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  useEffect(() => {
    // Load the most recent profile (recovery_email might have been set by admin)
    adminApi.me().then(({ data }) => {
      setRecovery(data.recovery_email || "");
      setFullName(data.full_name || "");
    }).catch(() => {});
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true); setProfileMsg(null);
    try {
      await adminApi.updateMe({ recovery_email: recovery, full_name: fullName });
      setProfileMsg({ ok: true, text: "Profil mis à jour." });
      if (refresh) refresh();
    } catch (err) {
      setProfileMsg({ ok: false, text: err?.response?.data?.detail || "Erreur" });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    setPwdMsg(null);
    // Length first (most specific error), then mismatch
    if (pwd.next.length < 8) {
      setPwdMsg({ ok: false, text: "Le nouveau mot de passe doit faire au moins 8 caractères." });
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdMsg({ ok: false, text: "La confirmation ne correspond pas." });
      return;
    }
    setSavingPwd(true);
    try {
      await adminApi.changePassword(pwd.current, pwd.next);
      setPwdMsg({ ok: true, text: "Mot de passe modifié avec succès." });
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwdMsg({ ok: false, text: err?.response?.data?.detail || "Erreur" });
    } finally {
      setSavingPwd(false);
    }
  };

  const onPwdChange = (field, value) => {
    setPwd((p) => ({ ...p, [field]: value }));
    if (pwdMsg && !pwdMsg.ok) setPwdMsg(null);
  };

  return (
    <div data-testid="admin-profile-page" className="max-w-2xl">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">/ mon compte</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">Profil</h1>
      </div>

      {/* Identity */}
      <section className="bg-white border border-primary/15 rounded-sm p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center">
            <UserCircle size={18} strokeWidth={1.7} />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-primary">Informations personnelles</h2>
            <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/50 mt-0.5">
              {user?.email} · {user?.role}
            </div>
          </div>
        </div>

        <form onSubmit={saveProfile} className="space-y-4">
          <Row label="Nom complet">
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                   className="w-full bg-transparent text-sm py-2 outline-none" data-testid="profile-fullname" />
          </Row>
          <Row label="Email de récupération (pour reset mot de passe)">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-foreground/40" />
              <input type="email" value={recovery} onChange={(e) => setRecovery(e.target.value)}
                     placeholder="bellinack@gmail.com"
                     className="flex-1 bg-transparent text-sm py-2 outline-none"
                     data-testid="profile-recovery-email" />
            </div>
          </Row>
          {profileMsg && (
            <div className={`text-xs px-3 py-2 rounded-sm ${profileMsg.ok ? "bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]" : "bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]"}`}
                 data-testid="profile-msg">
              {profileMsg.ok ? <CheckCircle2 size={12} className="inline mr-1 -mt-0.5" /> : <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />}
              {profileMsg.text}
            </div>
          )}
          <button type="submit" disabled={savingProfile}
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
                  data-testid="profile-save-btn">
            {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
        </form>
      </section>

      {/* Password */}
      <section className="bg-white border border-primary/15 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center">
            <KeyRound size={18} strokeWidth={1.7} />
          </div>
          <h2 className="font-display text-xl font-semibold text-primary">Changer le mot de passe</h2>
        </div>

        <form onSubmit={savePwd} className="space-y-4">
          <Row label="Mot de passe actuel" required>
            <input type="password" required value={pwd.current}
                   onChange={(e) => onPwdChange("current", e.target.value)}
                   className="w-full bg-transparent text-sm py-2 outline-none font-mono"
                   data-testid="profile-current-password" />
          </Row>
          <Row label="Nouveau mot de passe (min 8 car.)" required>
            <input type="password" required minLength={8} value={pwd.next}
                   onChange={(e) => onPwdChange("next", e.target.value)}
                   className="w-full bg-transparent text-sm py-2 outline-none font-mono"
                   data-testid="profile-new-password" />
          </Row>
          <Row label="Confirmer le nouveau mot de passe" required>
            <input type="password" required minLength={8} value={pwd.confirm}
                   onChange={(e) => onPwdChange("confirm", e.target.value)}
                   className="w-full bg-transparent text-sm py-2 outline-none font-mono"
                   data-testid="profile-confirm-password" />
          </Row>
          {pwdMsg && (
            <div className={`text-xs px-3 py-2 rounded-sm ${pwdMsg.ok ? "bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]" : "bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]"}`}
                 data-testid="password-msg">
              {pwdMsg.ok ? <CheckCircle2 size={12} className="inline mr-1 -mt-0.5" /> : <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />}
              {pwdMsg.text}
            </div>
          )}
          <button type="submit" disabled={savingPwd}
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
                  data-testid="profile-change-password-btn">
            {savingPwd ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Modifier le mot de passe
          </button>
        </form>
      </section>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
        {label}{required && <span className="text-[#E76F51]"> *</span>}
      </span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">{children}</div>
    </label>
  );
}
